import React, { useState } from 'react'
// libs
import { Editor, useEditorState } from '@tiptap/react'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
// icons
import TitleIcon from '@mui/icons-material/Title'
import FormatSizeIcon from '@mui/icons-material/FormatSize'
import FontDownloadIcon from '@mui/icons-material/FontDownload'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify'
// views
import { ButtonGroup, ToolbarButton, ToolbarWrapper } from './views'
// types
import { ToolbarOption } from 'components/FormElements/Wysiwyg/types'

const ALIGN_ICONS = {
  left: <FormatAlignLeftIcon />,
  center: <FormatAlignCenterIcon />,
  right: <FormatAlignRightIcon />,
  justify: <FormatAlignJustifyIcon />
}

const FONT_SIZES = ['8px', '10px', '12px', '14px', '16px', '18px', '24px', '30px', '36px']
const FONT_FAMILIES = ['Arial', 'Georgia', 'Impact', 'Roboto', 'Tahoma', 'Times New Roman', 'Verdana']

interface ToolbarProps {
  editor: Editor | null
  hidden?: boolean
  options?: ToolbarOption[] | readonly ToolbarOption[]
  customButtons?: React.ReactNode[]
}

const DEFAULT_OPTIONS: ToolbarOption[] = ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign']

const Toolbar: React.FC<ToolbarProps> = ({ editor, options = DEFAULT_OPTIONS, customButtons, hidden }) => {
  const [blockTypeAnchor, setBlockTypeAnchor] = useState<null | HTMLElement>(null)
  const [fontSizeAnchor, setFontSizeAnchor] = useState<null | HTMLElement>(null)
  const [fontFamilyAnchor, setFontFamilyAnchor] = useState<null | HTMLElement>(null)

  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return {
        bold: false, italic: false, underline: false, strike: false,
        bulletList: false, orderedList: false,
        alignLeft: false, alignCenter: false, alignRight: false, alignJustify: false,
        blockType: 'normal', fontSize: '', fontFamily: ''
      }
      return {
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        underline: e.isActive('underline'),
        strike: e.isActive('strike'),
        bulletList: e.isActive('bulletList'),
        orderedList: e.isActive('orderedList'),
        alignLeft: e.isActive({ textAlign: 'left' }),
        alignCenter: e.isActive({ textAlign: 'center' }),
        alignRight: e.isActive({ textAlign: 'right' }),
        alignJustify: e.isActive({ textAlign: 'justify' }),
        blockType:
          e.isActive('heading', { level: 1 }) ? 'h1' :
          e.isActive('heading', { level: 2 }) ? 'h2' :
          e.isActive('heading', { level: 3 }) ? 'h3' :
          e.isActive('heading', { level: 4 }) ? 'h4' :
          e.isActive('heading', { level: 5 }) ? 'h5' :
          e.isActive('heading', { level: 6 }) ? 'h6' :
          e.isActive('blockquote') ? 'blockquote' :
          'normal',
        fontSize: e.getAttributes('textStyle').fontSize ?? '',
        fontFamily: e.getAttributes('textStyle').fontFamily ?? ''
      }
    }
  })

  if (!editor || hidden) {
    return null
  }

  const has = (opt: ToolbarOption) => !hidden && options.includes(opt)

  const alignActive: Record<string, boolean> = {
    left: active.alignLeft,
    center: active.alignCenter,
    right: active.alignRight,
    justify: active.alignJustify
  }

  return (
    <ToolbarWrapper>
      {has('inline') && (
        <ButtonGroup withDivider>
          <ToolbarButton
            type='button'
            title='Bold'
            isActive={active.bold}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleBold().run()
            }}>
            <b>B</b>
          </ToolbarButton>
          <ToolbarButton
            type='button'
            title='Italic'
            isActive={active.italic}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleItalic().run()
            }}>
            <i>I</i>
          </ToolbarButton>
          <ToolbarButton
            type='button'
            title='Underline'
            isActive={active.underline}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleUnderline().run()
            }}>
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton
            type='button'
            title='Strikethrough'
            isActive={active.strike}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleStrike().run()
            }}>
            <s>S</s>
          </ToolbarButton>
        </ButtonGroup>
      )}

      {has('blockType') && (
        <ButtonGroup>
          <ToolbarButton
            type='button'
            title='Block type'
            isActive={active.blockType !== 'normal'}
            onMouseDown={(e) => {
              e.preventDefault()
              setBlockTypeAnchor(e.currentTarget)
            }}>
            <TitleIcon style={{ fontSize: 18 }} />
          </ToolbarButton>
          <Menu
            anchorEl={blockTypeAnchor}
            open={Boolean(blockTypeAnchor)}
            onClose={() => setBlockTypeAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            {(
              [
                { value: 'normal', label: 'Normal' },
                { value: 'h1', label: 'H1' },
                { value: 'h2', label: 'H2' },
                { value: 'h3', label: 'H3' },
                { value: 'h4', label: 'H4' },
                { value: 'h5', label: 'H5' },
                { value: 'h6', label: 'H6' },
                { value: 'blockquote', label: 'Blockquote' }
              ] as const
            ).map(({ value, label }) => (
              <MenuItem
                key={value}
                selected={active.blockType === value}
                onMouseDown={(e) => {
                  e.preventDefault()
                  setBlockTypeAnchor(null)
                  if (value === 'normal') editor.chain().focus().setParagraph().run()
                  else if (value === 'blockquote') editor.chain().focus().toggleBlockquote().run()
                  else {
                    const level = parseInt(value.replace('h', '')) as 1 | 2 | 3 | 4 | 5 | 6
                    editor.chain().focus().toggleHeading({ level }).run()
                  }
                }}>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </ButtonGroup>
      )}

      {has('fontSize') && (
        <ButtonGroup>
          <ToolbarButton
            type='button'
            title='Font size'
            isActive={Boolean(active.fontSize)}
            onMouseDown={(e) => {
              e.preventDefault()
              setFontSizeAnchor(e.currentTarget)
            }}>
            <FormatSizeIcon style={{ fontSize: 18 }} />
          </ToolbarButton>
          <Menu
            anchorEl={fontSizeAnchor}
            open={Boolean(fontSizeAnchor)}
            onClose={() => setFontSizeAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            {FONT_SIZES.map((s) => (
              <MenuItem
                key={s}
                selected={active.fontSize === s}
                onMouseDown={(e) => {
                  e.preventDefault()
                  setFontSizeAnchor(null)
                  editor.chain().focus().setFontSize(s).run()
                }}>
                {s.replace('px', '')}
              </MenuItem>
            ))}
          </Menu>
        </ButtonGroup>
      )}

      {has('fontFamily') && (
        <ButtonGroup withDivider>
          <ToolbarButton
            type='button'
            title='Font family'
            isActive={Boolean(active.fontFamily)}
            onMouseDown={(e) => {
              e.preventDefault()
              setFontFamilyAnchor(e.currentTarget)
            }}>
            <FontDownloadIcon style={{ fontSize: 18 }} />
          </ToolbarButton>
          <Menu
            anchorEl={fontFamilyAnchor}
            open={Boolean(fontFamilyAnchor)}
            onClose={() => setFontFamilyAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
            {FONT_FAMILIES.map((f) => (
              <MenuItem
                key={f}
                selected={active.fontFamily === f}
                onMouseDown={(e) => {
                  e.preventDefault()
                  setFontFamilyAnchor(null)
                  editor.chain().focus().setFontFamily(f).run()
                }}>
                {f}
              </MenuItem>
            ))}
          </Menu>
        </ButtonGroup>
      )}

      {has('list') && (
        <ButtonGroup withDivider>
          <ToolbarButton
            type='button'
            title='Bullet list'
            isActive={active.bulletList}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleBulletList().run()
            }}>
            <FormatListBulletedIcon style={{ fontSize: 16 }} />
          </ToolbarButton>
          <ToolbarButton
            type='button'
            title='Ordered list'
            isActive={active.orderedList}
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().toggleOrderedList().run()
            }}>
            <FormatListNumberedIcon style={{ fontSize: 16 }} />
          </ToolbarButton>
        </ButtonGroup>
      )}

      {has('textAlign') && (
        <ButtonGroup>
          {(['left', 'center', 'right', 'justify'] as const).map((align) => (
            <ToolbarButton
              key={align}
              type='button'
              title={`Align ${align}`}
              isActive={alignActive[align]}
              onMouseDown={(e) => {
                e.preventDefault()
                editor.chain().focus().setTextAlign(align).run()
              }}>
              {ALIGN_ICONS[align]}
            </ToolbarButton>
          ))}
        </ButtonGroup>
      )}

      {has('history') && (
        <ButtonGroup>
          <ToolbarButton
            type='button'
            title='Undo'
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().undo().run()
            }}>
            &#8617;
          </ToolbarButton>
          <ToolbarButton
            type='button'
            title='Redo'
            onMouseDown={(e) => {
              e.preventDefault()
              editor.chain().focus().redo().run()
            }}>
            &#8618;
          </ToolbarButton>
        </ButtonGroup>
      )}

      {customButtons && customButtons.length > 0 && <ButtonGroup>{customButtons}</ButtonGroup>}
    </ToolbarWrapper>
  )
}

export default Toolbar
